import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Phone, MessageSquare, Trash2, PhoneForwarded } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function MyNumbers() {
    const [numbers, setNumbers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [toggling, setToggling] = useState(null);
    const navigate = useNavigate();

    const load = async () => {
        try {
            const { data } = await api.get("/numbers/my");
            setNumbers(data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const release = async (n) => {
        if (!window.confirm(`Release ${n.phone_number}? This will permanently remove it from your account.`)) return;
        try {
            await api.post(`/numbers/release/${n.number_id}`);
            toast.success("Number released");
            load();
        } catch {
            toast.error("Couldn't release");
        }
    };

    const toggleForwarding = async (n) => {
        setToggling(n.number_id);
        try {
            const { data } = await api.patch(`/numbers/${n.number_id}/forwarding`, {
                enabled: !n.forward_to_cell,
            });
            toast.success(
                data.forward_to_cell
                    ? "Calls will now ring your cell phone"
                    : "Calls will go to Flex Line voicemail"
            );
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't update forwarding");
        } finally {
            setToggling(null);
        }
    };

    return (
        <div className="space-y-8" data-testid="my-numbers-page">
            <div className="flex items-end justify-between">
                <div>
                    <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-2">— Inventory</p>
                    <h1 className="font-display text-4xl font-bold tracking-tight">My numbers</h1>
                </div>
                <Button
                    onClick={() => navigate("/numbers")}
                    data-testid="buy-another-btn"
                    className="bg-black text-white rounded-sm h-10"
                >
                    Buy another
                </Button>
            </div>

            {loading && <div className="text-sm text-zinc-500">Loading…</div>}

            {!loading && numbers.length === 0 && (
                <div className="border border-zinc-200 rounded-sm p-12 text-center bg-zinc-50/50">
                    <Phone className="w-6 h-6 mx-auto text-accent-blue mb-3" />
                    <p className="font-display text-xl font-bold">No active numbers yet</p>
                    <p className="text-zinc-600 mt-2">Browse the marketplace to claim your first one.</p>
                </div>
            )}

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
                {numbers.map((n) => (
                    <div key={n.number_id} className="bg-white p-6" data-testid={`my-number-${n.number_id}`}>
                        <div className="flex items-start justify-between">
                            <Phone className="w-5 h-5 text-accent-blue" />
                            {n.demo && (
                                <span className="font-mono text-[9px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded-sm uppercase">
                                    Demo
                                </span>
                            )}
                        </div>
                        <div className="font-mono text-xl mt-4">{n.phone_number}</div>
                        <div className="text-xs text-zinc-500 mt-1">
                            {n.locality || n.region || n.country}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-4">
                            {n.capabilities.map((c) => (
                                <span key={c} className="font-mono text-[10px] px-1.5 py-0.5 border border-zinc-200 rounded-sm uppercase">
                                    {c}
                                </span>
                            ))}
                        </div>
                        <div className="mt-5 flex items-center justify-between border border-[#DDD6FE] rounded-sm px-3 py-2.5 bg-[#F7F9FC]">
                            <div className="flex items-center gap-2">
                                <PhoneForwarded className={`w-4 h-4 ${n.forward_to_cell ? "text-brand-green" : "text-zinc-400"}`} />
                                <span className="text-xs font-medium">Forward calls to my cell</span>
                            </div>
                            <button
                                onClick={() => toggleForwarding(n)}
                                disabled={toggling === n.number_id}
                                data-testid={`forwarding-toggle-${n.number_id}`}
                                role="switch"
                                aria-checked={!!n.forward_to_cell}
                                className={`relative w-10 h-6 rounded-full transition-colors ${
                                    n.forward_to_cell ? "bg-brand-green" : "bg-zinc-300"
                                }`}
                            >
                                <span
                                    className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                                        n.forward_to_cell ? "translate-x-[18px]" : "translate-x-0.5"
                                    }`}
                                    style={{ left: 0 }}
                                />
                            </button>
                        </div>
                        <div className="mt-4 flex gap-2">
                            <Button
                                onClick={() => navigate(`/inbox?number=${n.number_id}`)}
                                data-testid={`message-from-${n.number_id}`}
                                variant="outline"
                                className="flex-1 rounded-sm h-9 border-zinc-300"
                            >
                                <MessageSquare className="w-4 h-4 mr-2" /> Message
                            </Button>
                            <Button
                                onClick={() => release(n)}
                                data-testid={`release-${n.number_id}`}
                                variant="outline"
                                className="rounded-sm h-9 border-zinc-300 text-red-600 hover:bg-red-50"
                            >
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
